/**
 * Off-platform PayPal: customers pay the seller's PayPal directly.
 * BookAPT does not process or hold funds.
 */

const PAYPAL_HOSTS = ['paypal.com', 'paypal.me', 'www.paypal.com'];

export function isPayPalDirectPaymentUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return PAYPAL_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/**
 * Normalize seller input (handle, email, or full URL) to a payment URL.
 */
export function normalizePayPalDirectInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return isPayPalDirectPaymentUrl(trimmed) ? trimmed : null;
  }

  const withoutMe = trimmed.replace(/^paypal\.me\//i, '').replace(/^@/, '');
  const handleMatch = withoutMe.match(/^([a-zA-Z0-9._-]+)$/);
  if (handleMatch) {
    return `https://www.paypal.me/${encodeURIComponent(handleMatch[1])}`;
  }

  const emailMatch = trimmed.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  if (emailMatch) {
    const business = encodeURIComponent(emailMatch[0]);
    return `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${business}`;
  }

  return null;
}

/** Append amount for PayPal.me links when possible. */
export function buildPayPalPaymentUrl(
  baseUrl: string,
  amountUsd?: number
): string {
  if (amountUsd == null || !Number.isFinite(amountUsd) || amountUsd <= 0) {
    return baseUrl;
  }
  try {
    const parsed = new URL(baseUrl);
    const host = parsed.hostname.toLowerCase();
    if (host.includes('paypal.me')) {
      const path = parsed.pathname.replace(/\/$/, '');
      const amountSegment = `${amountUsd.toFixed(2)}USD`;
      if (path.endsWith(amountSegment)) {
        return baseUrl;
      }
      const basePath = path.replace(/\/\d+\.\d{2}USD$/i, '');
      return `https://www.paypal.me${basePath}/${amountSegment}`;
    }
  } catch {
    /* use base */
  }
  return baseUrl;
}

export function maskPayPalDirectUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('paypal.me')) {
      const handle = parsed.pathname.split('/').filter(Boolean)[0];
      return handle ? `paypal.me/${handle}` : 'paypal.me';
    }
    return parsed.hostname;
  } catch {
    return 'PayPal link';
  }
}
